// FASE 13 — Integrazioni JOBTIMEHR: stato per azienda (connessione, ultime sync,
// record acquisiti, errori). Solo metadata di integrazione, mai payload HR.
import { useCallback, useEffect, useState } from "react";
import { Cable, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS_META = {
  ACTIVE: "bg-green-100 text-green-800 border-green-300",
  DISABLED: "bg-slate-100 text-slate-600 border-slate-300",
  ERROR: "bg-red-100 text-red-800 border-red-300",
  "NON CONFIGURATA": "bg-slate-100 text-slate-500 border-slate-200",
};
const SYNC_META = {
  SUCCESS: "bg-green-100 text-green-800 border-green-300",
  ERROR: "bg-red-100 text-red-800 border-red-300",
  FAILED: "bg-red-100 text-red-800 border-red-300",
  PARTIAL: "bg-amber-100 text-amber-800 border-amber-300",
};

export default function PlatformIntegrations() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/platform/integrations/jobtimehr");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="platform-integrations">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Cable className="w-7 h-7 text-green-800" aria-hidden="true" /> Integrazioni · JOBTIMEHR
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Stato dell'integrazione per ogni azienda connessa. I dati HR importati conservano
          la provenance (JOBTIMEHR / MANUAL / DOCUMENT / CALCULATED): qui vedi solo lo stato tecnico.
        </p>
      </div>

      {error && <p className="text-sm text-red-700" data-testid="integrations-error">{error}</p>}
      {!data && !error && <p className="text-sm text-slate-600">Caricamento…</p>}

      {data && data.items.length === 0 && (
        <Card className="border-slate-200" data-testid="integrations-empty">
          <CardContent className="pt-6 pb-6 text-center">
            <p className="text-sm text-slate-600">Nessuna integrazione JOBTIMEHR configurata.</p>
            <p className="text-xs text-slate-500 mt-1">
              Le connessioni si attivano dalla scheda azienda del tenant, con credenziali cifrate.
            </p>
          </CardContent>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <div className="space-y-3">
          {data.items.map((it) => (
            <Card key={it.company_id} className="border-slate-200"
              data-testid={`integration-${it.company_id}`}>
              <CardContent className="pt-4 flex flex-wrap items-start gap-4">
                <div className="min-w-[220px]">
                  <p className="text-sm font-semibold text-slate-900">{it.ragione_sociale}</p>
                  <p className="text-[11px] text-slate-500 font-mono">
                    {it.external_company_id ? `conn: ${it.external_company_id}` : "connessione non configurata"}
                    {it.api_version ? ` · API ${it.api_version}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline"
                    className={`text-[10px] ${STATUS_META[it.connection_status] || STATUS_META["NON CONFIGURATA"]}`}
                    data-testid={`integration-status-${it.company_id}`}>
                    {it.connection_status}
                  </Badge>
                  {it.last_sync_status && (
                    <Badge variant="outline"
                      className={`text-[10px] ${SYNC_META[it.last_sync_status] || "bg-slate-100 text-slate-600"}`}
                      data-testid={`integration-sync-${it.company_id}`}>
                      ultima sync: {it.last_sync_status}{it.last_sync_mock ? " (mock)" : ""}
                    </Badge>
                  )}
                </div>
                <dl className="flex gap-6 text-xs text-slate-600 ml-auto">
                  <div><dt className="text-slate-400">Ultima sincronizzazione</dt>
                    <dd className="font-semibold text-slate-800">
                      {it.last_sync_at ? new Date(it.last_sync_at).toLocaleString("it-IT") : "Mai"}
                    </dd></div>
                  <div><dt className="text-slate-400">Record acquisiti</dt>
                    <dd className="font-semibold text-slate-800">{it.records_acquired}</dd></div>
                  <div><dt className="text-slate-400">Sync totali / fallite</dt>
                    <dd className={`font-semibold ${it.syncs_failed > 0 ? "text-red-700" : "text-slate-800"}`}>
                      {it.syncs_total} / {it.syncs_failed}
                    </dd></div>
                </dl>
                {it.last_error && (
                  <p className="w-full text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5"
                    data-testid={`integration-error-${it.company_id}`}>
                    Ultimo errore: {it.last_error.evento} — {it.last_error.dettagli}
                    {it.last_error.timestamp ? ` (${new Date(it.last_error.timestamp).toLocaleString("it-IT")})` : ""}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
        <RefreshCw className="w-3 h-3" aria-hidden="true" />
        La risincronizzazione si avvia dal contesto aziendale (JOBTIMEHR), non da questa console.
      </p>
    </div>
  );
}
